'use client'
import Link from 'next/link'
import baleIcon from '@/medias/Bale.png'
import telegramIcon from '@/medias/telegram.svg'
import enemadIcon from '@/medias/enemad.png'
import daneshbonyanIcon from '@/medias/daneshbonyan.png'
import parkIcon from '@/medias/park.png'
import { quickApps } from '@/data/quickApps'
import { databases } from '@/data/databases'
import { projectTypes } from '@/data/projectTypes'
import { hostingPathForSlug } from '@/lib/hosting'
import { useLang } from '@/context/LangContext'
import { t, footer as footerT } from '@/lib/translations'
import LogoLink from './LogoLink'

const productsLinks = [
  { labelKey: 'vps' as const, path: 'vps-server' },
  { labelKey: 'gpu' as const, path: 'gpu' },
  { labelKey: 'paas' as const, path: 'paas' },
  { labelKey: 'database' as const, path: 'database' },
  { labelKey: 'storage' as const, path: 'storage' },
  { labelKey: 'backup' as const, path: 'backup' },
  { labelKey: 'security' as const, path: 'security' },
  { labelKey: 'devops' as const, path: 'devops' },
  { labelKey: 'monitoring' as const, path: 'monitoring' },
  { labelKey: 'org' as const, path: 'organizations' },
  { labelKey: 'consulting' as const, path: 'consulting' },
  { labelKey: 'streaming' as const, path: 'streaming' },
  { labelKey: 'sentry' as const, path: 'sentry' },
  { labelKey: 'mcp' as const, path: 'mcp' },
]

const productLabels: Record<string, Record<string, string>> = {
  vps: { en: 'Cloud Servers (VPS)', fa: 'سرورهای ابری (VPS)', ar: 'خوادم سحابية (VPS)' },
  gpu: { en: 'GPU Servers', fa: 'سرورهای GPU', ar: 'خوادم GPU' },
  paas: { en: 'Cloud Platform (PaaS)', fa: 'پلتفرم ابری (PaaS)', ar: 'منصة سحابية (PaaS)' },
  database: { en: 'Managed Database', fa: 'دیتابیس مدیریت‌شده', ar: 'قاعدة بيانات مُدارة' },
  storage: { en: 'Cloud Storage (S3)', fa: 'ذخیره‌سازی ابری (S3)', ar: 'تخزين سحابي (S3)' },
  backup: { en: 'Backup', fa: 'پشتیبان‌گیری', ar: 'النسخ الاحتياطي' },
  security: { en: 'Security Services', fa: 'خدمات امنیتی', ar: 'الخدمات الأمنية' },
  devops: { en: 'DevOps Services', fa: 'خدمات دوآپس', ar: 'خدمات ديف أوبس' },
  monitoring: { en: 'Monitoring', fa: 'مانیتورینگ', ar: 'المراقبة' },
  org: { en: 'Organizational Services', fa: 'خدمات سازمانی', ar: 'الخدمات المؤسسية' },
  consulting: { en: 'Consulting', fa: 'مشاوره', ar: 'الاستشارات' },
  streaming: { en: 'Streaming Server', fa: 'سرور استریم', ar: 'خادم بث' },
  sentry: { en: 'Sentry', fa: 'سنتری', ar: 'سنتري' },
  mcp: {
    en: 'AI Assistants (MCP)',
    fa: 'دستیارهای هوش مصنوعی (MCP)',
    ar: 'مساعدو الذكاء الاصطناعي (MCP)',
  },
}

const companyLinks = [
  { label: 'aboutUs', path: 'about' },
  { label: 'blog', path: 'https://lexoya.com/blog' },
  { label: 'contactUs', path: 'contact' },
  { label: 'privacy', path: 'privacy' },
  { label: 'terms', path: 'terms' },
]

function TrustBadges({ className }: { className: string }) {
  return (
    <div className={`flex flex-col items-start gap-4 mt-6 ${className}`}>
      <div className="flex gap-4">
        <div style={{ backgroundColor: 'rgb(50 50 53 / 58%)' }} className="rounded-xl p-3 shadow-sm">
          <img
            src={daneshbonyanIcon.src}
            alt="Daneshbonyan"
            className="h-16 w-auto object-contain"
          />
        </div>
        <a
          referrerPolicy="origin"
          target="_blank"
          href="https://trustseal.enamad.ir/?id=545764&amp;Code=sRHkjzqQ2S62EIyDyjfRj4OoJJqxWAka"
          style={{ backgroundColor: 'rgb(50 50 53 / 58%)' }}
          className="rounded-xl p-3 shadow-sm block"
        >
          <img
            referrerPolicy="origin"
            src={enemadIcon.src}
            alt="e-namad"
            style={{ cursor: 'pointer' }}
            className="h-16 w-auto object-contain rounded-md"
          />
        </a>
      </div>
      <div style={{ backgroundColor: 'rgb(50 50 53 / 58%)' }} className="rounded-xl p-3 shadow-sm">
        <img
          src={parkIcon.src}
          alt="Pardis Technology Park"
          className="h-16 w-auto object-contain"
        />
      </div>
    </div>
  )
}

function LinkItem({ label, href, lang }: { label: string; href: string; lang: string }) {
  if (href.startsWith('http') || href.startsWith('#')) {
    return (
      <a
        href={href}
        className="text-xs text-[#64748b] hover:text-white transition-colors duration-200"
      >
        {label}
      </a>
    )
  }
  return (
    <Link
      href={`/${lang}/${href}`}
      className="text-xs text-[#64748b] hover:text-white transition-colors duration-200"
    >
      {label}
    </Link>
  )
}

function FooterColumn({
  lang: columnLang,
  title,
  links,
  href,
  viewAllLabel,
}: {
  lang: string
  title: string
  links: { label: string; href: string }[]
  href: string
  viewAllLabel?: string
}) {
  const useColumns = links.length > 10
  const rows = useColumns ? Math.ceil(links.length / 2) : links.length
  const showViewAll = viewAllLabel && rows > 10

  if (useColumns) {
    return (
      <div>
        <p className="text-sm font-semibold text-[var(--brand-400)] mb-4">{title}</p>
        <ul className="columns-2 gap-x-3">
          {links.map((link) => (
            <li key={link.label} className="mb-3 break-inside-avoid">
              <LinkItem label={link.label} href={link.href} lang={columnLang} />
            </li>
          ))}
        </ul>
        {showViewAll && (
          <p className="mt-2.5">
            <Link
              href={`/${columnLang}/${href}`}
              className="text-xs text-[var(--brand-400)] hover:text-white transition-colors duration-200"
            >
              {viewAllLabel} →
            </Link>
          </p>
        )}
      </div>
    )
  }

  return (
    <div>
      <p className="text-sm font-semibold text-[var(--brand-400)] mb-4">{title}</p>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.label}>
            <LinkItem label={link.label} href={link.href} lang={columnLang} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function Footer() {
  const lang = useLang()
  const quickAppsLinks = quickApps.map((app) => ({ label: app.name, href: 'quickapps' }))
  const databasesLinks = databases.map((db) => ({ label: db.name, href: 'database' }))
  const projectTypesLinks = projectTypes.map((pt) => ({
    label: pt.name,
    href: hostingPathForSlug(pt.slug),
  }))

  return (
    <footer className="relative border-t border-white/[0.04] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgb(var(--brand-rgb)/0.12),transparent)] pointer-events-none" />
      <div className="absolute inset-0 dot-pattern pointer-events-none opacity-25" />

      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgb(var(--brand-rgb)/0.05)] to-transparent bg-[length:200%_100%] animate-gradient-x pointer-events-none" />

      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[rgb(var(--brand-rgb)/0.3)] to-transparent pointer-events-none" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[rgb(var(--brand-rgb)/0.1)] to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2 md:col-span-1">
            <LogoLink className="inline-flex items-center mb-4" />
            <p className="text-sm text-[#64748b] leading-relaxed max-w-xs mb-4">
              {t(lang, footerT.tagline)}
            </p>
            <TrustBadges className="hidden md:flex" />
          </div>

          <div>
            <p className="text-sm font-semibold text-[var(--brand-400)] mb-4">{t(lang, footerT.products)}</p>
            <ul className="space-y-3">
              {productsLinks.map((link) => (
                <li key={link.labelKey}>
                  <Link
                    href={`/${lang}/${link.path}`}
                    className="text-xs text-[#64748b] hover:text-white transition-colors duration-200"
                  >
                    {productLabels[link.labelKey][lang] || productLabels[link.labelKey].en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <FooterColumn
            lang={lang}
            title={t(lang, footerT.quickApps)}
            links={quickAppsLinks}
            href="quickapps"
            viewAllLabel={`${t(lang, footerT.viewAll)} ${t(lang, footerT.quickApps)}`}
          />

          <FooterColumn
            lang={lang}
            title={t(lang, footerT.projectTypes)}
            links={projectTypesLinks}
            href="hosting"
            viewAllLabel={`${t(lang, footerT.viewAll)} ${t(lang, footerT.projectTypes)}`}
          />

          <FooterColumn
            lang={lang}
            title={t(lang, footerT.databases)}
            links={databasesLinks}
            href="database"
            viewAllLabel={`${t(lang, footerT.viewAll)} ${t(lang, footerT.databases)}`}
          />

          <div>
            <p className="text-sm font-semibold text-[var(--brand-400)] mb-4">{t(lang, footerT.explore)}</p>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <LinkItem label={t(lang, footerT[link.label as keyof typeof footerT])} href={link.path} lang={lang} />
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://ble.ir/themmr" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity duration-200">
                <img src={baleIcon.src} alt="Bale" className="w-7 h-7" />
              </a>
              <a href="https://t.me/lexoya_cloud" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity duration-200">
                <img src={telegramIcon.src} alt="Telegram" className="w-7 h-7" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/[0.04] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              href={`/${lang}/status`}
              className="text-xs text-[#475569] hover:text-white transition-colors duration-200 flex items-center gap-2"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]" />
              </span>
              {t(lang, footerT.servicesStatus)}
            </Link>
          </div>
          <p className="text-xs text-[#475569]">
            &copy; {new Date().getFullYear()} Lexoya, Inc. {t(lang, footerT.allRights)}
          </p>
        </div>

        <div className="mt-6 flex md:hidden items-center justify-center" dir="ltr">
          <TrustBadges className="md:hidden" />
        </div>
      </div>
    </footer>
  )
}
